import { BlobProvider } from "@react-pdf/renderer";
import Spinner from "components/Spinner";
import { useState } from "react";
import { Modal } from "react-bootstrap";
import authHeader from "services/auth-header";
import API from "services/axios";
import { parseAddress } from "pages/Sales/SplitShipments/splitAddress";
import { SplitShipmentPdf } from "./SplitShipmentPdf";

// Fetches what the slip needs (product images, the customer's location
// logo), then previews the packing slip with a Download button. Same image
// and logo sources as the Shipping module's PDF.

const getImage = async (path) => {
  if (!path) return null;
  try {
    const { data } = await API.post(
      "/design-request/get-image",
      { path },
      { headers: authHeader() },
    );
    return data || null;
  } catch {
    return null;
  }
};

const getLocationLogo = async (locationId) => {
  if (!locationId) return null;
  try {
    const { data } = await API.get("/locations", { headers: authHeader() });
    const location = data?.find?.((l) => l.id === locationId);
    return location?.pdfLogo ? await getImage(location.pdfLogo) : null;
  } catch {
    return null;
  }
};

/**
 * @param split      a split as the WO tab holds it: { splitId, shippingAddress,
 *                   carrier, trackingNo, status, notes, shipDate,
 *                   items: [{ woProductId, sizeKey, size, qty }] }
 * @param workOrder  { woId, jobName, customerName }
 * @param products   the WO's products from GET /split-shipments/wo/:woId
 * @param customer   { locationId } for the logo
 */
export default function SplitShipmentPdfButton({
  split,
  workOrder,
  products,
  customer,
  btnClasses,
  disabled,
}) {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState(null);

  const open = async () => {
    setShow(true);
    setLoading(true);
    try {
      const byProduct = new Map();
      for (const item of split.items || []) {
        if (!(item.qty > 0)) continue;
        const list = byProduct.get(Number(item.woProductId)) || [];
        list.push(item);
        byProduct.set(Number(item.woProductId), list);
      }
      const inSplit = products.filter((p) => byProduct.has(Number(p.id)));
      const [images, locationLogo] = await Promise.all([
        Promise.all(inSplit.map((p) => getImage(p.productImage))),
        getLocationLogo(customer?.locationId),
      ]);
      setDoc({
        split: { ...split, shippingAddress: parseAddress(split.shippingAddress) },
        workOrder,
        locationLogo,
        products: inSplit.map((p, i) => {
          const items = byProduct.get(Number(p.id));
          // Sizes in the work order's own order.
          const order = p.sizes.map((z) => z.key);
          return {
            ...p,
            image: images[i],
            sizes: items
              .slice()
              .sort((a, b) => order.indexOf(a.sizeKey) - order.indexOf(b.sizeKey))
              .map((it) => ({ size: it.size, qty: it.qty })),
          };
        }),
      });
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setShow(false);
    setDoc(null);
  };

  const download = (blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Packing-Slip-SP-${split.splitId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <button
        type="button"
        className={btnClasses || "btn btn-sm btn-outline-primary"}
        onClick={open}
        disabled={disabled}
      >
        <i className="mdi mdi-file-pdf-box me-1" />
        PDF
      </button>

      <Modal show={show} onHide={close} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Packing Slip -- SP-{split.splitId}</Modal.Title>
        </Modal.Header>
        {(loading || !doc) && (
          <Modal.Body>
            <div className="text-center py-5">
              <Spinner />
              <p className="mt-3">Loading images...</p>
            </div>
          </Modal.Body>
        )}
        {!loading && doc && (
          <BlobProvider document={<SplitShipmentPdf {...doc} />}>
            {({ blob, url, loading: building, error }) => (
              <>
                <Modal.Body>
                  {building && (
                    <div className="text-center py-5">
                      <Spinner />
                      <p className="mt-3">Generating PDF...</p>
                    </div>
                  )}
                  {error && (
                    <div className="alert alert-danger">
                      Error generating PDF: {error.message}
                    </div>
                  )}
                  {!building && !error && url && (
                    <iframe
                      src={url}
                      title="Packing slip preview"
                      style={{ width: "100%", height: "70vh" }}
                    />
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <button
                    className="btn btn-primary"
                    disabled={!blob}
                    onClick={() => download(blob)}
                  >
                    Download PDF
                  </button>
                  <button className="btn btn-secondary" onClick={close}>
                    Close
                  </button>
                </Modal.Footer>
              </>
            )}
          </BlobProvider>
        )}
      </Modal>
    </>
  );
}
